import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ASSETS, BRAND } from "@/lib/brand";
import { ArrowRight } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name.split(" ")[0]}`);
      navigate(user.role === "coach" ? "/coach" : "/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" style={{ background: "var(--bg)" }}>
      <div className="hidden lg:block relative overflow-hidden" style={{ background: "var(--surface-2)" }}>
        <img src={ASSETS.authorityCamel} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute top-8 left-8 right-8 flex justify-between items-center z-10">
          <Link to="/" className="flex items-baseline gap-2">
            <span className="font-script text-3xl text-white">{BRAND.name}</span>
            <span className="wordmark-tag text-white" style={{ color: "#FFFFFF" }}>{BRAND.tag}</span>
          </Link>
          <ThemeToggle />
        </div>
        <div className="absolute bottom-12 left-12 right-12 z-10 text-white">
          <p className="overline" style={{ color: "#D4CFC7" }}>Welcome back</p>
          <h2 className="font-display text-4xl mt-4 leading-tight" style={{ color: "#FFFFFF" }}>
            Step back into<br />
            <span className="font-script normal-case" style={{ fontSize: "1.15em", color: "#FFFFFF", letterSpacing: 0 }}>your practice.</span>
          </h2>
          <div className="bronze-rule mt-6" />
        </div>
      </div>

      <div className="flex flex-col justify-center px-8 md:px-16 py-12 max-w-xl w-full mx-auto">
        <div className="lg:hidden flex justify-between items-center mb-12">
          <Link to="/" className="flex items-baseline gap-2">
            <span className="font-script text-2xl">{BRAND.name}</span>
            <span className="wordmark-tag">{BRAND.tag}</span>
          </Link>
          <ThemeToggle />
        </div>
        <p className="overline">Sign in</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-6 leading-tight">Welcome,<br />
          <span className="font-script normal-case" style={{ fontSize: "1.1em", color: "var(--accent)", letterSpacing: 0 }}>beautiful.</span>
        </h1>
        <div className="bronze-rule mt-6" />

        <div className="mt-8 p-5" style={{ border: "1px solid var(--border)", background: "var(--surface-2)" }} data-testid="everfit-banner">
          <p className="font-label text-[10px] tracking-[0.26em] uppercase" style={{ color: "var(--accent)" }}>Current MTM members</p>
          <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>
            Your training currently lives in Everfit while our member portal is being finished.
          </p>
          <a href={BRAND.everfitUrl} target="_blank" rel="noreferrer" className="btn-secondary mt-4 text-xs" data-testid="everfit-link">
            Open Everfit onboarding <ArrowRight size={12} />
          </a>
        </div>

        <form onSubmit={onSubmit} className="mt-10 space-y-7" data-testid="login-form">
          <div>
            <label className="label-luxe" htmlFor="email">Email</label>
            <input id="email" data-testid="login-email" type="email" required className="input-luxe"
              value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@studio.com" />
          </div>
          <div>
            <label className="label-luxe" htmlFor="password">Password</label>
            <input id="password" data-testid="login-password" type="password" required className="input-luxe"
              value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="login-submit">
            {busy ? "Signing in..." : "Sign in"} <ArrowRight size={12} />
          </button>
        </form>

        <div className="my-10 flex items-center gap-4">
          <div className="h-px flex-1" style={{ background: "var(--border)" }} />
          <span className="font-label text-[10px] tracking-[0.26em] uppercase" style={{ color: "var(--text-tertiary)" }}>or</span>
          <div className="h-px flex-1" style={{ background: "var(--border)" }} />
        </div>

        <button onClick={googleLogin} className="btn-secondary w-full justify-center" data-testid="login-google">
          Continue with Google
        </button>

        <p className="mt-10 font-label text-xs tracking-wider" style={{ color: "var(--text-secondary)" }}>
          New here? <Link to="/apply" data-testid="link-register" className="text-accent hover:underline uppercase tracking-[0.2em]">Apply for coaching</Link>
        </p>
      </div>
    </div>
  );
}
